import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Copy, Download, ExternalLink, Loader2, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public share page for a file. Reachable at `/f/:slug`.
 *
 * Only public files resolve — a leaked slug for a file that was later flipped
 * to private returns 404. The page previews images inline, PDFs in an
 * <iframe>, videos + audio via native HTML5 players, and everything else via
 * a big "Open" button.
 *
 * NOTE: CRA doesn't do SSR, so real Twitter/Slack/Facebook scrapers won't see
 * the dynamic OG tags we set here. React-managed <title> + <meta> updates still
 * benefit real users and any scraper that runs JS (e.g. modern Twitter's bot).
 */
export default function SharePage() {
  const { slug } = useParams();
  const [file, setFile] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/files/f/${slug}`, { withCredentials: false });
        if (cancelled) return;
        setFile(r.data);
        setState('ok');
      } catch (err) {
        if (cancelled) return;
        setState(err?.response?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Dynamic <head> tags for real users + JS-executing scrapers.
  useEffect(() => {
    if (!file) return;
    const title = file.label || file.original_filename || 'Shared file · DowUrk';
    document.title = `${title} · DowUrk FramewUrk`;
    const set = (name, content, prop = 'name') => {
      let m = document.head.querySelector(`meta[${prop}="${name}"]`);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute(prop, name);
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };
    const downloadUrl = `${API}/files/${file.id}/download`;
    set('description', `Shared securely on DowUrk. ${file.category || 'File'} · ${humanSize(file.size)}`);
    set('og:title', title, 'property');
    set('og:type', 'article', 'property');
    set('og:url', window.location.href, 'property');
    set('og:description', `Shared securely on DowUrk. ${humanSize(file.size)}`, 'property');
    if (file.category === 'image') set('og:image', downloadUrl, 'property');
    set('twitter:card', file.category === 'image' ? 'summary_large_image' : 'summary');
    set('twitter:title', title);
  }, [file]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
          <p className="text-sm text-gray-500 mt-2">Loading shared file…</p>
        </div>
      </div>
    );
  }

  if (state === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="share-notfound">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Link expired or private</h1>
          <p className="text-sm text-gray-500 mt-1">
            This file either doesn&apos;t exist, has been removed, or is no longer public.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 mt-4 text-emerald-700 hover:underline text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to DowUrk
          </Link>
        </div>
      </div>
    );
  }

  const downloadUrl = `${API}/files/${file.id}/download`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch (_e) {
      toast.error('Could not copy — long-press the URL bar instead');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white p-4 sm:p-8" data-testid="share-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline mb-4">
          <ArrowLeft className="w-4 h-4" /> DowUrk FramewUrk
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
        >
          <div className="p-5 border-b border-gray-100">
            <h1 className="text-xl font-bold text-gray-900" data-testid="share-title">
              {file.label || file.original_filename || 'Shared file'}
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              {file.category} · {humanSize(file.size)}
            </p>
          </div>

          <div className="p-5 bg-gray-50/70">
            <Preview file={file} downloadUrl={downloadUrl} />
          </div>

          <div className="p-4 border-t border-gray-100 flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-2">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm"
                data-testid="share-open-btn"
              >
                <ExternalLink className="w-4 h-4" /> Open
              </a>
              <a
                href={downloadUrl}
                download={file.original_filename || file.id}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-700 text-sm"
                data-testid="share-download-btn"
              >
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-700 text-sm"
              data-testid="share-copy-btn"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>
        </motion.div>
        <p className="text-center text-xs text-gray-400 mt-4">
          Shared securely on DowUrk FramewUrk.
        </p>
      </div>
    </div>
  );
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function Preview({ file, downloadUrl }) {
  if (file.category === 'image') {
    return (
      <img
        src={downloadUrl}
        alt={file.label || file.original_filename || 'Shared'}
        className="w-full max-h-[520px] object-contain rounded-md bg-white"
        referrerPolicy="no-referrer"
      />
    );
  }
  if (file.category === 'video') {
    return (
      <video src={downloadUrl} controls className="w-full max-h-[520px] rounded-md bg-black">
        <track kind="captions" />
      </video>
    );
  }
  if (file.category === 'audio') {
    return <audio src={downloadUrl} controls className="w-full" />;
  }
  if (file.content_type === 'application/pdf') {
    return (
      <iframe
        src={downloadUrl}
        title={file.original_filename || 'Shared PDF'}
        className="w-full h-[520px] rounded-md bg-white border border-gray-100"
      />
    );
  }
  return (
    <div className="p-8 bg-white rounded-md border border-dashed border-gray-200 text-center text-gray-600 text-sm">
      This file type doesn&apos;t preview inline. Use <strong>Open</strong> or <strong>Download</strong> above.
    </div>
  );
}
